/**
 * Shared benefit-row model + glyphs for the "Blood tests" prototype.
 *
 * Single source of truth for the comparison-card benefit rows so the
 * membership card AND the membership details popup render the exact same
 * rows (check marks, discount-rosette glyphs, and the −CHF price chips).
 * Kept in its own module to avoid a circular import between
 * `BloodTrackingCards` and `card/MembershipDetailsModal`.
 */
import { useTranslation } from "react-i18next";
import { languageMap, BOOKING_NS } from "@/i18n";
import { BookingLanguage } from "@/types/booking";

/** Olive check in a soft-green disc — the "included" marker. */
export const CheckDisc = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center rounded-full bg-[#DBF0DC96]">
    <svg
      width="12"
      height="8"
      viewBox="0 0 13 8"
      fill="none"
      aria-hidden
      className="overflow-visible"
    >
      <path
        d="M0 4 4.444 8 13 0"
        fill="none"
        stroke="#408C49"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  </span>
);

/** Member-discount "rosette" glyph (priced rows). */
export const DiscountGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 19 19"
    fill="none"
    aria-hidden
    className="flex-shrink-0"
  >
    <path
      fillRule="evenodd"
      fill="#408A49"
      d="M9.5 19c-.361 0-.706-.068-1.033-.203a2.66 2.66 0 0 1-.871-.585c-.502-.497-.934-.821-1.293-.974-.36-.154-.899-.23-1.618-.23-.748 0-1.384-.262-1.907-.785a2.6 2.6 0 0 1-.786-1.908c0-.712-.077-1.25-.23-1.614-.154-.363-.478-.796-.974-1.297a2.66 2.66 0 0 1-.587-.871A2.57 2.57 0 0 1 0 9.502c0-.357.067-.699.201-1.027.134-.328.33-.621.587-.879.496-.498.82-.927.974-1.288.153-.361.23-.902.23-1.623 0-.748.262-1.384.786-1.908a2.6 2.6 0 0 1 1.907-.785c.712 0 1.25-.077 1.614-.23.363-.154.796-.478 1.297-.974.254-.255.544-.45.872-.585A2.57 2.57 0 0 1 9.496 0c.358 0 .701.067 1.029.201.328.134.621.33.879.587.498.496.927.82 1.288.974.361.153.902.23 1.623.23.748 0 1.384.262 1.908.785.523.524.785 1.16.785 1.908 0 .712.077 1.25.23 1.614.154.363.478.796.974 1.297.257.258.453.55.587.877.134.327.201.669.201 1.025 0 .357-.067.699-.201 1.027a2.66 2.66 0 0 1-.587.879c-.497.502-.821.934-.974 1.293-.154.36-.23.899-.23 1.618 0 .748-.262 1.384-.785 1.908a2.6 2.6 0 0 1-1.908.785c-.712 0-1.25.077-1.614.23-.363.154-.796.478-1.297.974a2.66 2.66 0 0 1-.871.585A2.57 2.57 0 0 1 9.5 19Zm2.435-5.836c.345 0 .636-.119.873-.356.237-.237.355-.528.355-.873 0-.346-.118-.639-.355-.881-.237-.241-.528-.362-.873-.362-.346 0-.639.121-.881.362-.241.242-.362.535-.362.881 0 .345.121.636.362.873.242.237.535.356.881.356ZM7.017 13.027l6.01-6-1.054-1.054-6 6.01 1.044 1.044Zm.929-5.081c.241-.242.362-.535.362-.881 0-.345-.121-.636-.362-.873-.237-.237-.528-.355-.881-.355-.345 0-.636.118-.873.355-.237.237-.356.528-.356.873 0 .346.119.639.356.881.237.237.528.358.873.358.353 0 .644-.121.881-.358Z"
    />
  </svg>
);

/** Blood-tube glyph in a soft-green disc. */
const TubeDisc = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center rounded-full bg-[#DBF0DC96]">
    <svg width="9" height="13" viewBox="0 0 13 19" fill="none" aria-hidden>
      <path
        fillRule="evenodd"
        fill="#408B49"
        d="M6.5 19c-1.294 0-2.396-.451-3.307-1.352-.911-.901-1.366-1.995-1.366-3.283V5.327c-.505 0-.936-.177-1.292-.53A1.74 1.74 0 0 1 0 3.51V1.827C0 1.322.178.891.535.535.891.178 1.322 0 1.827 0h9.336c.505 0 .938.178 1.297.535.36.356.54.787.54 1.292V3.51c0 .505-.18.934-.54 1.287-.359.353-.792.53-1.297.53v9.183c-.038 1.255-.509 2.317-1.414 3.186C8.845 18.565 7.762 19 6.5 19ZM1.827 3.827h9.336c.09 0 .169-.031.236-.092a.31.31 0 0 0 .101-.225V1.827a.32.32 0 0 0-.101-.231.33.33 0 0 0-.236-.096H1.827a.33.33 0 0 0-.231.096.32.32 0 0 0-.096.231V3.51c0 .089.032.165.096.225a.32.32 0 0 0 .231.092Zm6.914 12.764c.615-.605.922-1.347.922-2.226v-.375H6.567v-1.499h3.096v-2.424H6.567v-1.5h3.096V5.327H3.327v9.038c0 .879.309 1.621.928 2.226.618.606 1.367.909 2.245.909.878 0 1.625-.303 2.241-.909Z"
      />
    </svg>
  </span>
);

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex flex-shrink-0 items-center rounded-[40px] bg-[#DFE9FA] px-3 py-0.5 text-[11px] font-medium uppercase leading-4 tracking-[0.1px] text-[#001A51]">
    {children}
  </span>
);

interface BenefitRow {
  /** i18n key under the `bloodTracking.benefit.*` namespace. */
  key: string;
  /** Membership-side glyph. Defaults to the green check disc. */
  icon?: "discount" | "tube";
  /** i18n key for the member-only chip (membership card only). */
  chip?: string;
  /** Interpolation value passed as `{{amount}}` to the chip key. */
  chipAmount?: number;
  /** Included on the one-off baseline card? Defaults to false (muted dash). */
  baseline?: boolean;
}

export const BENEFIT_ROWS: BenefitRow[] = [
  { key: "bloodTracking.benefit.advancedTest", baseline: true },
  { key: "bloodTracking.benefit.reportActionPlan", baseline: true },
  { key: "bloodTracking.membership.bloodTestsPerYear" },
  {
    key: "bloodTracking.benefit.memberDiscountMri",
    icon: "discount",
    chip: "bloodTracking.benefit.memberDiscountMriChip",
    chipAmount: 259,
  },
  {
    key: "bloodTracking.benefit.memberPricingAddons",
    icon: "discount",
    chip: "bloodTracking.benefit.memberPricingAddonsChip",
    chipAmount: 34,
  },
  { key: "bloodTracking.benefit.yearRoundSupport" },
  { key: "bloodTracking.benefit.followUpReminders" },
  { key: "bloodTracking.benefit.uploadLabData" },
];

/** Membership benefit rows — all included, with discount glyphs + price chips. */
export const MembershipBenefitList = ({
  className = "",
  language,
}: {
  className?: string;
  language: BookingLanguage;
}) => {
  const { t } = useTranslation(BOOKING_NS, { lng: languageMap[language] });
  return (
    <ul className={`flex flex-col ${className}`}>
      {BENEFIT_ROWS.map((row) => (
        <li key={row.key} className="flex min-h-9 items-center gap-3">
          {row.icon === "discount" ? (
            <DiscountGlyph />
          ) : row.icon === "tube" ? (
            <TubeDisc />
          ) : (
            <CheckDisc />
          )}
          <span className="text-sm/5 text-[#2D2D2D]">{t(row.key)}</span>
          {row.chip && <Chip>{t(row.chip, { amount: row.chipAmount })}</Chip>}
        </li>
      ))}
    </ul>
  );
};
